/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value-coldef
 * @desc Service for managing column definitions in SmartTable
 */

import { colDefBulkSelect } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/bulk-select';
import { colDefAddColumn } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/entity/add-column';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/metadata';
import { getMetadataMoleculeColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/metadata/molecule';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/amount';
import { getProtocolValueNameColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/name';
import { getProtocolValueResourceColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resource';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resourceItem';
import { getProtocolValueResourceLocationColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resourceLocation';
import { getProtocolValueResourceItemStatusColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/status';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { forEach } from 'lodash';

export class GridSmartTableProtocolValueColDefService {
  public static getDefaultColIds = (
    entity: Experiment | Protocol,
  ) => {
    const defaultColdefs = ['name', 'amount', 'resource_item'];

    // Add resource/resourceItem metadatas

    return defaultColdefs;
  };

  public static getColDefs = (
    entityView: EntityView,
    experimentOrProtocol: Experiment | Protocol,
    fullscreen: boolean = false,
  ): SmartTableColDef[] => {
    const colDefs: SmartTableColDef[] = fullscreen
      ? []
      : [colDefBulkSelect];

    if (!entityView.column_definition_ids) {
      return colDefs;
    }

    forEach(entityView.column_definition_ids, (colId: string) => {
      if (colId === 'name') {
        colDefs.push(
          getProtocolValueNameColumnDef(experimentOrProtocol),
        );
      }
      if (colId === 'resource_item') {
        colDefs.push(
          getProtocolValueResourceItemColumnDef(experimentOrProtocol),
        );
      }
      if (colId === 'resource') {
        colDefs.push(
          getProtocolValueResourceColumnDef(experimentOrProtocol),
        );
      }
      if (colId === 'amount') {
        colDefs.push(
          getProtocolValueAmountColumnDef(experimentOrProtocol),
        );
      }
      if (colId === 'resource_location') {
        colDefs.push(
          getProtocolValueResourceLocationColumnDef(
            experimentOrProtocol,
          ),
        );
      }
      if (colId === 'resource_item_status') {
        colDefs.push(
          getProtocolValueResourceItemStatusColDef(
            experimentOrProtocol,
          ),
        );
      }
      if (
        colId.split(':').length >= 5 &&
        colId.split(':')[2] === 'molecule'
      ) {
        const parts = colId.split(':');
        const parentEntityClass = parts[1];
        const type = parts[2];
        const metadataLabel = parts[3];
        const field = parts[4];
        const protocolValueName = parts[5];
        const moleculeColDef = getMetadataMoleculeColDef(
          experimentOrProtocol,
          parentEntityClass,
          type as MetadataType,
          metadataLabel,
          field,
          protocolValueName,
        );

        if (moleculeColDef) {
          colDefs.push(moleculeColDef);
        }
      } else if (colId.startsWith('metadata:')) {
        if (colId.startsWith('metadata:experiment:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Experiment.entityName,
            type,
            label || '',
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:protocol:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Protocol.entityName,
            type,
            label || '',
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:resource:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const protocolValueName =
            Metadata.getProtocolValueNameFromColId(colId);

          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Resource.entityName,
            type,
            label || '',
            protocolValueName,
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:resource_item:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const protocolValueName =
            Metadata.getProtocolValueNameFromColId(colId);

          const colDef = getMetadataColDef(
            experimentOrProtocol,
            ResourceItem.entityName,
            type,
            label || '',
            protocolValueName,
          );
          colDefs.push(colDef);
        }
      }
    });

    if (fullscreen) {
      colDefs.push(colDefAddColumn);
    }

    return colDefs;
  };
}
